import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FooterCellRenderer, FooterCellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/footer.interface';
import { GroupedPayment } from 'app/shared/interfaces/payment.interface';

/**
 * Parameters for Payment Footer Cell Renderer
 */
export interface PaymentFooterCellRendererParams {
  field: 'totalPaying' | 'totalOverPayment' | 'computed';
  decimals?: number;
}

/**
 * Payment Footer Cell Renderer
 *
 * Reactive footer renderer that recalculates totals on every change detection.
 * Accesses LIVE data from params.filteredData to always show current totals.
 *
 * Supported fields:
 * - 'totalPaying': Sum of all totalPaying values
 * - 'totalOverPayment': Sum of all totalOverPayment values
 * - 'computed': Sum of totalPaying + totalOverPayment (grand total)
 *
 * @example
 * ```typescript
 * footer: {
 *   footerCellRenderer: PaymentFooterCellRendererComponent,
 *   footerCellRendererParams: {
 *     field: 'totalPaying',
 *     decimals: 2
 *   },
 *   footerClass: 'px-6 text-right font-bold'
 * }
 * ```
 */
@Component({
  selector: 'app-payment-footer-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span class="font-bold text-right">{{ formattedTotal$ | async }}</span>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      text-align: right;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentFooterCellRendererComponent implements FooterCellRenderer<GroupedPayment>, OnInit {
  params!: FooterCellRendererParams<GroupedPayment>;

  /**
   * Observable that emits formatted total string
   */
  formattedTotal$!: Observable<string>;

  private readonly decimalPipe = new DecimalPipe('en-US');

  /**
   * Get custom params from footer config
   */
  get customParams(): PaymentFooterCellRendererParams | undefined {
    return this.params?.params as PaymentFooterCellRendererParams;
  }

  ngOnInit(): void {
    // Get Observable stream from params (fallback to static array if not available)
    const data$ = this.params.data$ || new Observable<GroupedPayment[]>(subscriber => {
      subscriber.next(this.params.filteredData || this.params.data || []);
      subscriber.complete();
    });

    const field = this.customParams?.field;
    const decimals = this.customParams?.decimals ?? 2;

    // Create Observable that calculates and formats total
    this.formattedTotal$ = data$.pipe(
      map(data => {
        if (!field || data.length === 0) {
          return '0.00';
        }

        // Calculate total based on field type
        let total = 0;
        switch (field) {
          case 'totalPaying':
            total = data.reduce((sum, row) => sum + (row.totalPaying ?? 0), 0);
            break;

          case 'totalOverPayment':
            total = data.reduce((sum, row) => sum + (row.totalOverPayment ?? 0), 0);
            break;

          case 'computed':
            total = data.reduce((sum, row) =>
              sum + (row.totalPaying ?? 0) + (row.totalOverPayment ?? 0),
              0
            );
            break;

          default:
            return '0.00';
        }

        // Format with decimal places
        const format = `1.${decimals}-${decimals}`;
        return this.decimalPipe.transform(total, format) ?? '0.00';
      })
    );
  }
}
